import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IDocument, getFileIcon, getFileCategory, FileCategory } from 'src/app/models/document.models';
import { EventColors } from 'src/app/models/event-colors';

@Component({
  selector: 'app-document-card',
  templateUrl: './document-card.component.html',
  styleUrls: ['./document-card.component.scss'],
})
export class DocumentCardComponent implements OnChanges {
  @Input() document: IDocument;
  @Input() colors: EventColors;
  @Input() language: string;
  @Input() viewMode: 'grid' | 'list' = 'list';
  @Input() selected: boolean = false;
  @Input() selectionEnabled: boolean = true;

  @Output() preview = new EventEmitter<IDocument>();
  @Output() selectionChange = new EventEmitter<boolean>();

  _selected: boolean = false;

  constructor(private cdr: ChangeDetectorRef, private translate: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('document' in changes || 'selected' in changes) {
      this._selected = this.selected;
    }
  }

  get iconSrc(): string {
    return getFileIcon(this.document?.type);
  }

  get category(): FileCategory {
    return getFileCategory(this.document?.type);
  }

  get isImage(): boolean {
    return this.category === FileCategory.Image;
  }

  get categoryColor(): string {
    const map: Record<string, string> = {
      Word: '#2B579A',
      Excel: '#217346',
      PowerPoint: '#D24726',
      PDF: '#F40F02',
      Image: '#7B1FA2',
      Media: '#0288D1',
    };
    return map[this.category] || '#757575';
  }

  get formattedSize(): string {
    if (!this.document?.size) return '';
    const bytes = this.document.size;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  onClick(): void {
    this.preview.emit(this.document);
  }

  onCheckboxClick(event: Event): void {
    event.stopPropagation();
    this._selected = !this._selected;
    this.selectionChange.emit(this._selected);
  }

  get selectAriaLabel(): string {
    return this.translate.instant('pages.documents.selection.select_all');
  }

  syncSelected(selectedIds: Set<string>): void {
    this._selected = selectedIds.has(this.document?.uid);
  }

  onThumbnailError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }
}
